import { useCallback, useEffect, useRef } from 'react';

import toast from '../utils/toast';
import Icon from '../components/Icon';
import styles from './Base.module.scss';

/**
 * BaseModal - Pure presentational modal component
 * 
 * Used by:
 * - Base.js (wraps this with URL-based dialog management)
 * - Products dialogs (uses directly with prop-based onClose)
 * 
 * @param {string} title - Modal title
 * @param {function} onClose - Callback when modal is closed
 * @param {React.ReactNode} children - Modal content
 */
const BaseModal = ({ title, onClose, children }) => {

  const ref = useRef(null);

  /*
   * BLUR (click outside modal)
   */

  const _blur = useCallback((event) => {

    const target = event.target;

    if (toast.contains(target)) {
      return;
    }

    if (target.classList.contains('skip')) {
      return;
    }

    if (ref.current && !ref.current.contains(event.target)) {
      onClose?.();
    }

  }, [onClose]);

  useEffect(() => {
    document.addEventListener('click', _blur, true);
    return () => document.removeEventListener('click', _blur, true);
  }, [_blur]);

  /*
   * ESCAPE (escape key press)
   */

  const _esc = useCallback((event) => {
    if (event.key === 'Escape') {
      onClose?.();
    }
  }, [onClose])

  useEffect(() => {
    document.addEventListener('keyup', _esc, true);
    return () => document.removeEventListener('keyup', _esc, true);
  }, [_esc]);

  /*
   * Body scroll lock
   */

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  return (
    <>
      <div className={styles.mask}>
      </div>

      <div className={styles.area}>
        <div
          ref={ref} 
          role='dialog'
          aria-modal={true}
          aria-labelledby='heading'
          className={styles.modal}>

          <button
            className={styles.close}
            onClick={() => onClose?.()}
            aria-label='close'>

            <Icon name='close' />

          </button>

          <h2 id='heading' className={styles.title}>
            {title}
          </h2>

          <div className={styles.content}>
            {children}
          </div>

        </div>
      </div>
    </>
  );
};

export default BaseModal;

